import Handlebars from "utilities/vendor/handlebars/4.7.6/js/handlebars";
import hbs from "utilities/vendor/handlebars/4.7.6/js/handlebars";
/**
 * Helper to render all the modules within a given layout area.
 * @file
 */

/* eslint-disable strict */

'use strict';

/**
 * Decodes html entities.
 * @param {string} input The input to decode
 * @returns {string} The decoded html entity.
 */
function htmlDecode(input) {
  return typeof input !== 'string' ? input : input
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>');
}

/**
 * Extracts all text from the given markup into the provided property on "object".
 * @param {string} markup The markup to extract from.
 * @param {RegExp} regexp A regular expression to match strings to remove.
 * @param {object} object The object containing a property to store the extracted text.
 * @param {string} prop The property on "object" to srie the extract text.
 * @param {string} join A seperator to join extracted contents.
 * @returns {object} The orignally passed in object.
 */
function extract(markup, regexp, object, prop, join = '\n') {
  const config = object;
  const inlined = [];

  const extracted = markup.replace(regexp, ($0, $1) => {
    const trimmed = $1.trim();
    if (trimmed) inlined.push(trimmed);
    return '';
  });

  config[prop] = `${config[prop] || ''}${join}${inlined.join(join)}`;
  return extracted;
}

/**
 * Generates the [data-module-parameters] data-attribute.
 * @param {object} context The rendering context.
 * @returns {string} The stringified module parameters.
 */
function generateModuleParameterDataAttribute(context) {
  const moduleParameters = {};

  // Get actual parameters that have been set and also default parameter values
  context.module.moduleParams.forEach((param) => {
    moduleParameters[param.name] = Object.hasOwnProperty.call(context.module || {}, param.name)
      ? context.module[param.name]
      : param.default;
  });

  return `data-module-parameters='${JSON.stringify(moduleParameters).replace(/'/g, '&#39;')}'`;
}

/**
 * Attempts to parse a value as JSON. If fails, it returns the original value.
 * @param {string} value The value to attempt to parse.
 * @returns {string} The parsed value.
 */
function maybeJsonParse(value) {
  try { return JSON.parse(value); } catch (e) { return value; }
}

/**
 * Recursively walks the given input object and renders all strings with the given context.
 * @param {object} input The object to walk/render.
 * @param {object} context The Handlebars rendering context.
 * @returns {object} The originally passed in object.
 */
function recursivelyRenderExpressions(input, context) {
  if (!input || typeof input !== 'object') return input;

  const object = input;
  Object.keys(object).forEach((key) => {
    const value = object[key];

    // If It's a string with {{, render it as a Handlebars template.
    // Else if it's an object, call this method recursively.
    if (typeof value === 'string' && value.indexOf('{{') > -1) {
      object[key] = maybeJsonParse(htmlDecode(Handlebars.compile(value)(context)));
    } else {
      object[key] = recursivelyRenderExpressions(object[key], context);
    }
  });

  return object;
}

/**
 * Renders a module.
 * @param {string} env The page's environment.
 * @param {string} language The page's language.
 * @param {object} siteConfig The site's JSON configuration.
 * @param {object} pageConfig The page's JSON configuration.
 * @param {object} platformModule The module's JSON configuration.
 * @param {object} rules Page rules.
 * @param {string} moduleType The module's type (i.e. "vue").
 * @returns {stirng} The rendered module html.
 */
function renderModule(env, language, siteConfig, pageConfig, platformModule, rules, moduleType) {
  const { config } = platformModule;
  const dataSources = platformModule.dataSources || {};

  const { moduleName } = config;
  const moduleVersion = config.version;

  // This is the Handlebars rendering context
  // used to render the module's html with.
  const context = {
    env,
    language,
    moduleName,
    global: siteConfig.global[language],
    page: pageConfig.parameters,
    site: siteConfig.params,
    module: platformModule.params,
    children: platformModule.children,
    footnotes: pageConfig.footnotes,
  };

  // Populate context data source(s) on the context
  // based on the current language value.
  Object.keys(dataSources).forEach((name) => {
    context[name] = dataSources[name][language];
  });

  // Walk module parameter JSON object and replace all Handlebars
  // expressions with content from the rendering context.
  recursivelyRenderExpressions(platformModule.params || {}, context);

  // The tag type for the module's enclosing element and it's id="" attribute.
  const tag = config.tag || 'div';
  const id = platformModule.id || config.id || '';

  // Makes the ID of the container available in the template being rendered
  context.module.containerID = id;

  // The modules scss namespace and its "versioned" namespace
  const nameSpace = config.nameSpace || moduleName;
  const modNameSpaceVersion = `${nameSpace}-class-v-${moduleVersion}`.replace(/\./g, '-');

  let dataInit = '';
  let spaModuleInit = '';
  let dataOptions = '';

  if (config.data && typeof config.data.init === 'string') {
    dataInit = `data-init="${config.data.init}"`;
    spaModuleInit = 'spa-module-init';
  }

  // Insert data options from processed context
  if (context.module.data && typeof context.module.data.options === 'object') {
    dataOptions = `data-options='${JSON.stringify(context.module.data.options)}'`;
  }

  // Detemine the module's require loop load type
  const group = (!platformModule.primaryloadType && pageConfig.hasSecondary)
    ? 'secondary'
    : 'primary';
  const dataSpartaLoad = `data-sparta-load="${group}"`;
  const dataRef = `data-module-ref="${platformModule.name}"`;
  (context.module.load || {}).group = group;

  // Process and create module header html.
  // This will resolve any template bar syntax used in module config data.
  const wrapper = Handlebars.compile(`
    <${tag}
      ${id ? `id="${id}"` : ''}
      data-component="module"
      data-module="${moduleName}"
      data-module-container="3.1.4"
      data-version="${moduleVersion}"
      ${dataSpartaLoad}
      ${dataRef}
      ${dataOptions}
      ${dataInit}
      ${generateModuleParameterDataAttribute(context)}
      class="${modNameSpaceVersion} ${nameSpace} ${spaModuleInit}"
    >
  `)(context);

  // Render the module's inner "context" html using context
  let html = '';
  if (moduleType !== 'vue') {
    if (!rules[moduleName] || rules[moduleName].indexOf('addModule') === -1) {
      html = Handlebars.compile(`{{>${moduleName}}}`)(context);

      // Extract module in-line CSS and JS to move to page head tag and foot respectively
      if (siteConfig.params.inlineCode && !platformModule.isOnDemandModule) {
        html = extract(html, /<script>([\s\S]*?)<\/script>/g, pageConfig, 'inlineJS', ';');
        html = extract(html, /<style>([\s\S]*?)<\/style>/g, pageConfig, 'inlineCSS', '\n');
      }
    }
  }

  return `${wrapper}\n${html}\n</${tag}>\n\n`;
}

const helpers = {
  /**
   * Render a module's HTML
   * @param {object} siteConfig Site JSON configuration
   * @param {object} pageConfig Page JSON Configuration
   * @param {string} language The page's language.
   * @param {string} env The current build environment.
   * @param {string} assetPath The local html path to assets (images, etc.).
   * @param {object} moduleArray An array of module objects.
   * @returns {string} The rendered module html markup.
   */
  moduleContainer(siteConfig, pageConfig, language, env, assetPath, moduleArray) {
    return moduleArray
      .map(mod => helpers.composeModule(siteConfig, pageConfig, language, env, assetPath, mod))
      .join('');
  },

  /**
   * Render a module's HTML
   * @param {object} siteConfig Site JSON configuration
   * @param {object} pageConfig Page JSON Configuration
   * @param {string} language The page's language.
   * @param {string} env The current build environment.
   * @param {string} assetPath The local html path to assets (images, etc.).
   * @param {object} platformModule An object with a lot of data from the platform.
   * @param {string} moduleType The module's type (i.e. "vue").
   * @returns {string} The rendered module html markup.
   */
  composeModule(siteConfig, pageConfig, language, env, assetPath, platformModule, moduleType) {
    if (!siteConfig.global) throw new Error('The site\'s global configuration is undefined.');

    const rules = {};
    const { childModules } = platformModule;

    // Extract rules by name
    if (Array.isArray(platformModule.rules)) {
      platformModule.rules.forEach((rule) => {
        rules[platformModule.config.moduleName] = rule.actions.map(action => action.type);
      });
    }

    // Rendering child modules, if any.
    if (Array.isArray(childModules)) {
      const children = {};
      childModules.forEach((childModule) => {
        const id = childModule.id
          || childModule.config.shortName
          || childModule.config.id
          || childModule.config.moduleName;

        children[id] = renderModule(
          env,
          language,
          siteConfig,
          pageConfig,
          childModule,
          rules,
          moduleType,
        );
      });

      Object.assign(platformModule, { children });
    }

    // Rendering regular modules...
    return renderModule(env, language, siteConfig, pageConfig, platformModule, rules, moduleType);
  },
};

// Export helpers
module.exports.register = (Handlebars) => {
  Object.keys(helpers).forEach(key => Handlebars.registerHelper(key, helpers[key]));
};

module.exports.helpers = helpers;
